import React from "react";

/* The app's loading placeholder.

   WHY IT REPLACES <LoadingBox/>
   Every listing screen used to answer a pending fetch with one fixed-position
   spinner pinned to the middle of the viewport (`.loading { position: fixed;
   top: 50%; left: 50% }`). Three things were wrong with that: it sat over the
   sidebar and the page chrome rather than over the thing that was loading, it
   collapsed the table to nothing so the page jumped by a few hundred pixels
   the moment data landed, and with two screens' worth of rows the jump moved
   the row you were reaching for. A skeleton answers all three - it is drawn
   in the same box the content will occupy, so the layout is already the right
   height when the response arrives and nothing moves.

   THE PRIMITIVE
   `<Skeleton/>` is one bar: a background tint plus a shimmer sweep, sized by
   the caller. Everything else here is a composition of it shaped like the
   content it stands in for - rows for a table, a label/control pair for a
   form, a tile for a card grid.

   ACCESSIBILITY
   The bars are decoration, so each one is `aria-hidden`. The wrapper carries
   `role="status"` plus an off-screen "Loading" label, which is what a screen
   reader announces - one polite message instead of a hundred empty boxes.
   Table rows cannot take a wrapper without breaking `<tbody>`, so there the
   rows are hidden and the caller's table gets `aria-busy`.

   MOTION
   The sweep is an enhancement: `prefers-reduced-motion: reduce` drops the
   animation and leaves the flat tint, which still reads as "not content yet".

   HANDING OVER (Fluid Functionalism's reveal-swap, theme.css)
   Never swap a skeleton out and the data in on the same frame: the data fades
   in from nothing, so the space blinks empty. <SkeletonSwap/> and
   <SkeletonTable/> keep the skeleton mounted and add the real block after it
   in the same grid cell; the skeleton fades out as the block fades in (a
   table hands over row by row). A lone value replacing an inline bar has no
   room for that and takes `className="reveal-in"` instead.
*/

/** `12` -> `"12px"`, `"60%"` -> `"60%"`. */
const size = (value) =>
  typeof value === "number" ? value + "px" : value || undefined;

/**
 * One placeholder bar.
 *
 *   width   css length or number of px; defaults to filling its parent
 *   height  css length or number of px; defaults to a text line
 *   radius  css length or number of px; defaults to the app's small radius
 *   circle  square the box off and round it fully (avatars, icon buttons)
 */
export const Skeleton = ({
  width,
  height,
  radius,
  circle = false,
  className = "",
  style,
}) => (
  <span
    className={("sk" + (circle ? " sk-circle" : "") + " " + className).trim()}
    aria-hidden="true"
    style={{
      width: size(width),
      height: size(height),
      borderRadius: circle ? "50%" : size(radius),
      ...style,
    }}
  />
);

/**
 * A paragraph of bars. `widths` sets each line individually - the default
 * shortens the last line, which is what makes a stack read as prose instead
 * of as a block.
 */
export const SkeletonText = ({
  lines = 3,
  widths,
  height = 12,
  className = "",
  label = "Loading",
}) => (
  <span
    className={("sk-text " + className).trim()}
    role="status"
    aria-label={label}
  >
    {Array.from({ length: lines }, (_, index) => (
      <Skeleton
        key={index}
        height={height}
        width={
          widths
            ? widths[index % widths.length]
            : index === lines - 1
            ? "62%"
            : "100%"
        }
      />
    ))}
  </span>
);

/**
 * Placeholder rows for a `<tbody>`. Returns bare `<tr>`s, so it is spread
 * straight into the table that is waiting:
 *
 *   <tbody>{isLoading ? <SkeletonRows rows={10} columns={6}/> : realRows}</tbody>
 *
 * `columns` is either a count or an array of per-column widths, which is
 * worth the extra characters on a table whose first column is a name and
 * whose last is a 24px action glyph.
 */
export const SkeletonRows = ({ rows = 8, columns = 5, cellClassName = "" }) => {
  const widths = Array.isArray(columns)
    ? columns
    : Array.from({ length: columns }, (_, index) =>
        index === 0 ? "70%" : index % 3 === 0 ? "45%" : "60%"
      );

  return (
    <>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <tr key={rowIndex} className="sk-row" aria-hidden="true">
          {widths.map((width, cellIndex) => (
            <td key={cellIndex} className={cellClassName}>
              <Skeleton width={width} height={12} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
};

/**
 * Label-over-control pairs, for the edit forms that fetch the record they are
 * about to show. `columns` is the Bootstrap grid split the real form uses, so
 * the placeholder lands on the same column lines.
 */
export const SkeletonForm = ({
  fields = 6,
  columns = 2,
  actions = true,
  className = "",
  label = "Loading form",
}) => (
  <div
    className={("sk-form " + className).trim()}
    role="status"
    aria-label={label}
  >
    <div className="row">
      {Array.from({ length: fields }, (_, index) => (
        <div
          key={index}
          className={columns === 1 ? "col-md-12" : "col-md-" + 12 / columns}
        >
          <div className="sk-field">
            <Skeleton width="38%" height={11} />
            <Skeleton height={38} radius="var(--app-radius-sm)" />
          </div>
        </div>
      ))}
    </div>
    {actions ? (
      <div className="sk-form-actions">
        <Skeleton width={104} height={38} radius="var(--app-radius-sm)" />
        <Skeleton width={104} height={38} radius="var(--app-radius-sm)" />
      </div>
    ) : null}
  </div>
);

/**
 * An image that holds its box while the bytes arrive. The placeholder is the
 * same bar as everywhere else, absolutely filling the frame, and the <img>
 * only becomes visible once it has decoded - so a slow thumbnail grid fades
 * in tile by tile instead of reflowing on every load event.
 */
export const ImageWithSkeleton = ({
  src,
  alt,
  width = "100%",
  height = "100%",
  className = "",
  onClick,
}) => {
  const [isLoaded, setIsLoaded] = React.useState(false);

  return (
    <span
      className={("sk-image " + className).trim()}
      style={{ width: size(width), height: size(height) }}
    >
      {/* stays under the image while it fades in, so the frame never blinks
          empty; it fades out and stops shimmering once the bytes are in */}
      <Skeleton className={"sk-fill" + (isLoaded ? " is-done" : "")} />
      <img
        src={src}
        alt={alt}
        className="gallery-image"
        loading="lazy"
        onLoad={() => setIsLoaded(true)}
        onError={() => setIsLoaded(true)}
        onClick={onClick}
        style={{ opacity: isLoaded ? 1 : 0 }}
      />
    </span>
  );
};

/**
 * A skeleton that crossfades into the block it stands in for.
 *
 *   <SkeletonSwap loaded={!isLoading} skeleton={<SkeletonForm fields={5}/>}>
 *     <form>…</form>
 *   </SkeletonSwap>
 *
 * Both sit in one grid cell; once `loaded`, the real block is added after
 * the skeleton, which fades out and stops counting toward the height. Each
 * side gets its own wrapper so a fragment or plain text works on either.
 *
 *   as         wrapper element ("div"; "span" for inline content)
 *   items      true: both sides are `reveal-items`, so their children hand
 *              over one by one (tile grids, lists)
 *   className  extra classes on each side's wrapper (e.g. the grid class)
 */
export const SkeletonSwap = ({
  loaded,
  skeleton,
  children,
  as: Tag = "div",
  items = false,
  className = "",
}) => {
  const side = ((items ? "reveal-items " : "") + className).trim() || undefined;
  const hadSkeleton = useHadSkeleton(!loaded);
  return (
    <Tag className="reveal-swap" aria-busy={loaded ? undefined : true}>
      {hadSkeleton ? (
        <Tag className={side} aria-hidden="true">
          {skeleton}
        </Tag>
      ) : null}
      {loaded ? <Tag className={side}>{children}</Tag> : null}
    </Tag>
  );
};

/* True from the first render that is loading on. A block that mounts with
   its data already there (an add form, a cached list) never draws a skeleton
   just to fade it out. Set during render: derived state, no extra paint. */
const useHadSkeleton = (loading) => {
  const [had, setHad] = React.useState(loading);
  if (loading && !had) setHad(true);
  return had || loading;
};

/**
 * A table whose skeleton rows hand over to the real rows one by one.
 *
 *   <SkeletonTable
 *     loading={isLoading}
 *     className="table table-sm"
 *     head={<><colgroup>…</colgroup><thead>…</thead></>}
 *     rows={8}
 *     columns={7}
 *   >
 *     {realRows}   (or the empty-state <tr>)
 *   </SkeletonTable>
 *
 *   prefix  rows at the top of the body that are not data - a row of filter
 *           inputs. Always shown, never refetched away.
 *   suffix  rows after the data - an in-table pagination row. Stays mounted
 *           (an uncontrolled paginator keeps its page) but is invisible while
 *           loading, so it is not stranded above the skeleton.
 *
 * Two tables share one grid cell, each in a div (max-height, which lets a
 * replaced skeleton stop holding the height, does nothing to a <table>):
 *
 *   1. the skeleton - an invisible copy of `head` and `prefix`, so the bars
 *      sit on the real rows' lines, then the skeleton rows
 *   2. the real table - `head`, `prefix`, the data rows, `suffix`
 *
 * The real table never unmounts, so its head, its filter inputs (and the
 * focus in them) and its sort state survive every refetch; only the data
 * rows come and go. When `loading` turns false the skeleton rows fade out a
 * step apart as the real rows fade in.
 */
export const SkeletonTable = ({
  loading,
  className = "",
  head = null,
  prefix = null,
  suffix = null,
  rows = 8,
  columns = 5,
  children,
}) => {
  const hadSkeleton = useHadSkeleton(Boolean(loading));
  return (
  <div className={"reveal-swap reveal-swap-table" + (loading ? " is-loading" : "")}>
    {hadSkeleton ? (
    <div className="reveal-swap-skeleton" aria-hidden="true">
      <table className={className}>
        {head}
        <tbody>
          {prefix}
          <SkeletonRows rows={rows} columns={columns} />
        </tbody>
      </table>
    </div>
    ) : null}
    <div>
      <table className={className} aria-busy={loading ? true : undefined}>
        {head}
        <tbody className="reveal-items">
          {prefix}
          {loading ? null : children}
          {React.Children.map(suffix, (row) =>
            React.isValidElement(row)
              ? React.cloneElement(row, {
                  className: ((row.props.className || "") + " reveal-swap-suffix").trim(),
                })
              : row
          )}
        </tbody>
      </table>
    </div>
  </div>
  );
};

export default Skeleton;
